import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';
import { 
    collection,
    query,
    onSnapshot,
    addDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    where, 
    orderBy, 
    getDocs,
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

// --- CONSTANTS ---
const PENDING_REVIEW_STATUSES_FIREBASE = [
    "PENDING_DIGITAL_CHECKIN",
];

const ADDONS_LIST = [
    'breakfast', 'parking', 'airportTransfer', 'extraBed', 
    'roomUpgrade', 'spaAppointment'
];

// --- UTILITY FUNCTIONS ---
const generateGuestKey = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let res = '';
    for (let i = 0; i < 6; i++) {
        res += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return res;
};

const safeToDate = (timestampOrString) => {
    if (!timestampOrString) return null;
    if (typeof timestampOrString === 'object' && timestampOrString.seconds) {
        const date = timestampOrString.toDate();
        return isNaN(date.getTime()) ? null : date;
    }
    if (timestampOrString instanceof Date) {
        return isNaN(timestampOrString.getTime()) ? null : timestampOrString;
    }
    const parsedDate = new Date(timestampOrString);
    if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
    }
    return null; 
};

const initialFinancialState = {
    roomPriceOverride: 0.00,
    accommodationPrepaid: true, // NEW FIELD: Default to prepaid
    staffAccommodationPrice: 0.00, // NEW FIELD: Amount entered by staff if not prepaid
    taxRate: 0.1,
    touristTax: 0.0,
    depositRequired: 0.0,
    addOnsPrice: 0.0,
    adjustedFinalPrice: 0.0,
    assignedRoomId: '',
    assignedRoomNumber: '',
    assignedRoomType: '',
    roomAccessType: 'Digital Door Code',
    doorCode: '',
    paymentStatus: 'AWAITING_PAYMENT',
    paymentMethod: 'Credit Card',
    guestType: 'Leisure',
    billingNotes: '',
    checkInStaffId: '',
    staffAddons: {
        breakfast: false, parking: false, airportTransfer: false, 
        extraBed: false, roomUpgrade: false, spaAppointment: false
    }
};

const calculateFinalPrice = (registration, finance) => {
    // Determine the base room price per night
    let basePricePerNight = 0;
    
    if (finance.accommodationPrepaid) {
        // If prepaid, the base price is 0. Only calculate taxes/add-ons.
        basePricePerNight = 0;
    } else {
        // If NOT prepaid, use the staff-entered accommodation price.
        // For direct bookings, it might default to roomPriceOverride, but staffAccommodationPrice takes precedence if set.
        basePricePerNight = finance.staffAccommodationPrice || finance.roomPriceOverride || registration.roomPrice || 0;
    }

    if (basePricePerNight <= 0 && !finance.accommodationPrepaid) {
        // Only return 0 if it's NOT prepaid AND the price is zero (error state or free room)
        // If prepaid, subTotalRoom is 0, but we still calculate Addons/TouristTax.
    }
    
    const checkInDate = safeToDate(registration.arrivalTime) || new Date();
    const checkOutDate = safeToDate(registration.departureTime) || new Date(checkInDate.getTime() + 86400000);

    // Calculate number of days/nights
    const dayCount = Math.max(1, Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / 86400000));
    
    // Total room accommodation charge
    const subTotalRoom = basePricePerNight * dayCount;
    
    // Total variable charges
    const totalAddons = finance.addOnsPrice;
    const totalTouristTax = finance.touristTax * dayCount;
    
    // Base total (Accommodation + Addons + Tourist Tax)
    const baseTotal = subTotalRoom + totalAddons + totalTouristTax;

    // Apply main tax rate to the taxable base total (Accommodation + Addons)
    // Assuming Tourist Tax is non-taxable (common practice)
    const taxableBase = subTotalRoom + totalAddons;
    const totalTax = taxableBase * finance.taxRate;

    const finalPrice = baseTotal + totalTax;
    
    return parseFloat(finalPrice.toFixed(2));
};
// --- END UTILITY FUNCTIONS ---


export default function PendingGuestReview({ 
    pendingCount, 
    onAccept, 
    onReject 
}) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [pendingGuests, setPendingGuests] = useState([]);
    const [availableRooms, setAvailableRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const [guestToApprove, setGuestToApprove] = useState(null); 
    const [financialData, setFinancialData] = useState(initialFinancialState);

    const hotelId = user?.hotelId;

    // --- FETCH LOGIC (omitted for brevity, assume unchanged) ---
    const fetchAvailableRooms = useCallback(async () => {
        if (!hotelId) return;
        try {
            const roomsRef = collection(db, 'rooms');
            const q = query(roomsRef, 
                where('hotelId', '==', hotelId), 
                where('status', '==', 'available') 
            );
            const querySnapshot = await getDocs(q);
            const rooms = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAvailableRooms(rooms);
        } catch (err) {
            console.error("Error fetching available rooms:", err);
            setError("Could not load available rooms for assignment.");
        }
    }, [hotelId]);

    const fetchPendingGuests = useCallback(() => {
        if (!hotelId) {
            setError(t('errors.hotel_id_missing', "Hotel ID missing."));
            setLoading(false);
            return () => {}; 
        }

        const q = query(
            collection(db, 'guestRegistrations'),
            where('hotelId', '==', hotelId),
            orderBy('submittedAt', 'asc')
        ); 

        const unsubscribe = onSnapshot(q, snapshot => {
            const allGuests = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            const pending = allGuests.filter(g =>
                g.status && PENDING_REVIEW_STATUSES_FIREBASE.includes(g.status)
            );
            setPendingGuests(pending);
            setLoading(false);
        }, err => {
            console.error("Error loading pending guests:", err);
            setError(t('reception.error_query_failed'));
            setLoading(false);
        });

        return unsubscribe;
    }, [hotelId]);

    useEffect(() => {
        fetchAvailableRooms();
        const unsub = fetchPendingGuests();
        return () => unsub && unsub();
    }, [fetchPendingGuests, fetchAvailableRooms]);
    // --- END FETCH LOGIC ---
    
    // --- HANDLERS ---
    const handleInitiateApproval = (guest) => {
        setGuestToApprove(guest);
        
        const initialRoomPrice = guest.path === 'direct' ? (guest.roomPrice || 0) : 0; 
        const guestAddonsTotal = ADDONS_LIST.reduce((sum, key) => sum + (guest[key] ? 10 : 0), 0);
        
        // Determine initial prepaid status based on booking path (3rd party is usually prepaid)
        const isPrepaid = guest.path !== 'direct';
        
        const initialFinance = {
            ...initialFinancialState,
            roomPriceOverride: initialRoomPrice, 
            staffAccommodationPrice: isPrepaid ? 0.00 : initialRoomPrice, // Initialize staff price
            accommodationPrepaid: isPrepaid, // Set initial prepaid status
            addOnsPrice: guestAddonsTotal,
            checkInStaffId: user?.uid,
            assignedRoomId: guest.roomId || '',
            assignedRoomNumber: guest.roomNumber || '',
            assignedRoomType: guest.roomType || '',
        };

        const calculatedPrice = calculateFinalPrice(guest, initialFinance);
        setFinancialData({
            ...initialFinance,
            adjustedFinalPrice: calculatedPrice,
        });
    };
    
    const handleCloseForm = () => {
        setGuestToApprove(null);
    };

    const handleFinancialChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        let val;
        
        if (type === "checkbox") {
            val = checked;
        } 
        else if (
            name === "roomPriceOverride" ||
            name === "staffAccommodationPrice" ||
            name === "taxRate" ||
            name === "touristTax" ||
            name === "depositRequired" ||
            name === "addOnsPrice"
        ) {
            // FIX: This robust parsing logic handles the 'type="text"' fields correctly.
            
            // Allow only digits and a single decimal point
            let rawValue = value.replace(/[^0-9.]/g, ''); 
            
            // Prevent multiple decimal points
            const parts = rawValue.split('.');
            if (parts.length > 2) {
                // If more than one dot, keep the first part and the dot, and only the characters of the second part
                rawValue = parts[0] + '.' + parts.slice(1).join('');
            }

            // If the last character is a dot, we keep it as a string to allow for typing decimals (e.g., '12.')
            if (rawValue.endsWith('.')) {
                val = rawValue;
            } else {
                // Otherwise, parse the cleaned value to a float, default to 0 if invalid/empty
                val = parseFloat(rawValue) || 0; 
            }
        } 
        else {
            val = value;
        }

        setFinancialData(prev => {
            // Use the raw string value (if it contains a trailing dot) for display
            const updatedValue = typeof val === 'string' ? val : (
                name === "taxRate" || name === "touristTax" ? parseFloat(val) : val
            );

            const updated = { ...prev, [name]: updatedValue };
            
            // Special handling for accommodationPrepaid toggle
            if (name === 'accommodationPrepaid') {
                if (val) {
                    // If switching to Prepaid, room charge is 0
                    updated.staffAccommodationPrice = 0.00;
                } else {
                    // If switching to Not Prepaid, default to the original/overridden price
                    const defaultPrice = prev.roomPriceOverride || guestToApprove.roomPrice || 0;
                    updated.staffAccommodationPrice = defaultPrice;
                }
            }

            // Calculate final price using the *parsed float value* of the accommodation price fields
            const dataForCalculation = {
                ...updated,
                roomPriceOverride: parseFloat(updated.roomPriceOverride) || 0,
                staffAccommodationPrice: parseFloat(updated.staffAccommodationPrice) || 0
            };

            if (['roomPriceOverride', 'taxRate', 'touristTax', 'addOnsPrice', 'accommodationPrepaid', 'staffAccommodationPrice'].includes(name)) {
                updated.adjustedFinalPrice = calculateFinalPrice(guestToApprove, dataForCalculation);
            }
            return updated;
        });
    };

    const handleRoomAssignmentChange = (e) => {
        const selectedRoomId = e.target.value;
        const selectedRoom = availableRooms.find(room => room.id === selectedRoomId);
        
        setFinancialData(prev => {
            const updated = { ...prev, 
                assignedRoomId: selectedRoomId,
                assignedRoomNumber: selectedRoom ? selectedRoom.roomNumber : '',
                assignedRoomType: selectedRoom ? selectedRoom.type : '',
                // For direct bookings, link price override to the assigned room price
                roomPriceOverride: (guestToApprove.path === 'direct' && selectedRoom) 
                    ? parseFloat(selectedRoom.price) 
                    : prev.roomPriceOverride,
            };

            // Update staffAccommodationPrice based on new room price if not prepaid
            if (!updated.accommodationPrepaid && selectedRoom) {
                updated.staffAccommodationPrice = parseFloat(selectedRoom.price);
            } else if (!updated.accommodationPrepaid && !selectedRoom) {
                updated.staffAccommodationPrice = 0.00;
            }

            // Ensure calculation uses float values for affected fields
            const dataForCalculation = {
                ...updated,
                roomPriceOverride: parseFloat(updated.roomPriceOverride) || 0,
                staffAccommodationPrice: parseFloat(updated.staffAccommodationPrice) || 0
            };

            updated.adjustedFinalPrice = calculateFinalPrice(guestToApprove, dataForCalculation);
            return updated;
        });
    };
    
    const handleStaffAddonToggle = (key) => {
        setFinancialData(prev => {
            const updatedStaffAddons = { ...prev.staffAddons, [key]: !prev.staffAddons[key] };
            // Simple logic: assume each toggled addon costs $10
            const newAddonsTotal = ADDONS_LIST.reduce((sum, k) => sum + (updatedStaffAddons[k] ? 10 : 0), 0);
            
            const updated = {
                ...prev,
                staffAddons: updatedStaffAddons,
                addOnsPrice: newAddonsTotal,
            };
            
            // Ensure calculation uses float values for affected fields
            const dataForCalculation = {
                ...updated,
                roomPriceOverride: parseFloat(updated.roomPriceOverride) || 0,
                staffAccommodationPrice: parseFloat(updated.staffAccommodationPrice) || 0
            };

            updated.adjustedFinalPrice = calculateFinalPrice(guestToApprove, dataForCalculation);
            return updated;
        });
    };
    
    const handleFinalizeApproval = async (e) => {
        e.preventDefault();
        if (!guestToApprove) return;

        setLoading(true);
        setError('');
        setSuccess('');

        // Use parsed float values for validation and final data
        const finalStaffAccommodationPrice = parseFloat(financialData.staffAccommodationPrice) || 0;
        const finalRoomPriceOverride = parseFloat(financialData.roomPriceOverride) || 0;


        if (!financialData.assignedRoomId) {
            setError("🚨 Please assign a room before checking in the guest.");
            setLoading(false);
            return;
        }

        try {
            const key = financialData.doorCode || generateGuestKey().toUpperCase(); // Use doorCode if set, otherwise generate AccessKey

            const finalData = {
                ...guestToApprove,
                ...financialData,
                roomId: financialData.assignedRoomId,
                roomNumber: financialData.assignedRoomNumber,
                roomType: financialData.assignedRoomType,
                // Ensure the final price used for billing in the DB is calculated based on the staff's settings
                roomPriceOverride: finalRoomPriceOverride, // Store the final override price as a float
                staffAccommodationPrice: finalStaffAccommodationPrice, // Store the final staff price as a float
                roomPrice: finalStaffAccommodationPrice || 0, // This is the *actual* amount being charged to the guest for the room (per night)
                status: 'CHECKED_IN',
                checkInDate: serverTimestamp(),
                accessKey: key, 
                checkOutStaffId: null,
                hotelId,
            };

            delete finalData.id;
            
            const guestRef = await addDoc(collection(db, "guests"), finalData);
            const guestId = guestRef.id;

            await updateDoc(doc(db, 'rooms', finalData.roomId), {
                status: 'occupied',
                available: false,
                currentGuestId: guestId,
            });

            await setDoc(doc(collection(db, "chats"), guestId), {
                hotelId,
                guestId,
                guestName: finalData.fullName,
                roomNumber: finalData.roomNumber,
                lastMessageText: `Welcome! Your check-in is complete. Final bill: $${financialData.adjustedFinalPrice.toFixed(2)}. Access key: ${key}`,
                lastMessageTimestamp: serverTimestamp(),
                guestHasUnread: true,
            });

            await deleteDoc(doc(db, 'guestRegistrations', guestToApprove.id));

            setSuccess(t('reception.checkin_success', { 
                name: guestToApprove.fullName, 
                room: financialData.assignedRoomNumber, 
                key: key 
            }));
            handleCloseForm(); 
            fetchAvailableRooms();
        } catch (err) {
            console.error(err);
            setError(t('errors.approval_failed') + ": " + err.message);
        }

        setLoading(false);
    };

    const handleRejectGuest = async (id, name) => {
        if (!window.confirm(t('reception.confirm_reject', { name: name }))) return;

        try {
            await deleteDoc(doc(db, 'guestRegistrations', id));
            setSuccess(t('reception.reject_success', { name: name }, `${name} removed.`));        } catch (err) {
            setError(t('errors.reject_failed'));
        }
    };
    // --- END HANDLERS ---

    // ===============================
    // UI Render
    // ===============================
    if (loading) return <p>{t('common.loading')}</p>;
    if (error) return <p className="error-message info-box" style={{backgroundColor: '#ffeaea', color: '#cc0000', border: '1px solid #cc0000'}}>{error}</p>;

    const displayGuests = pendingGuests.filter(g => g.status && g.submittedAt);

    return (
        <div style={{ padding: '20px' }}>
            <h2>{t('reception.pending_checkins')}  ({displayGuests.length})</h2>
            {success && <p className="success-message info-box" style={{ backgroundColor: '#e9f7ef', color: '#28a745', padding: '10px', borderRadius: '5px' }}>{success}</p>}
            
            {pendingGuests.length !== displayGuests.length && (
                <div className="info-box" style={{backgroundColor: '#fff0cc', color: '#8a6d3b', border: '1px solid #faebcc', marginBottom: '20px'}}>
                    <p>⚠️ **{t('reception.debug_warning')}:** {pendingGuests.length - displayGuests.length} documents are malformed (missing `status` or `submittedAt`). The issue is likely in the **GuestRegistrationForm** creation logic.</p>
                </div>
            )}

            {displayGuests.length === 0 ? (
                <p style={{ textAlign: "center", padding: 20, backgroundColor: "#fafafa" }}>
                    {t('reception.no_pending_checkins')}
                </p>
            ) : (
                <ul style={{ listStyle: "none", padding: 0 }}>
  {displayGuests.map((guest) => {
    // 1. Date and Form State
    const checkInDate = safeToDate(guest.arrivalTime);
    const checkInTimeString = checkInDate ? checkInDate.toLocaleString() : 'N/A';
    const isFormOpen = guestToApprove && guestToApprove.id === guest.id;

    // 2. FIXED SOURCE LOGIC: Specifically looking for the 'supplier' field
    const isDirect = guest.path === 'direct';
    
    // We check guest.supplier first since you confirmed that's where it is
    const providerName = !isDirect 
        ? (guest.supplier || guest.source || guest.path || '3rd Party') 
        : t('reception.direct');

    // 3. HELPERS FOR DETAILED FORM (To prevent no-undef errors)
    const requestedAddons = ADDONS_LIST.filter(addon => guest[addon]);
    const formatAddonName = (name) => name.replace(/([A-Z])/g, ' $1').trim();

    return (
        <React.Fragment key={guest.id}>
            <li style={{
                padding: '15px 20px',
                marginBottom: '12px',
                border: isFormOpen ? "2px solid #007bff" : "1px solid #eee", 
                borderRadius: '10px',
                display: "flex",
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: isFormOpen ? '#f0f7ff' : '#fff',
                boxShadow: '0 2px 5px rgba(0,0,0,0.03)'
            }}>
                {/* LEFT SECTION: Info & Modern Badge */}
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '5px' }}>
                        <strong style={{ fontSize: '1.15rem', color: '#2c3e50' }}>{guest.fullName}</strong>
                        
                        {/* THE MODERN BADGE */}
                        <span style={{
                            padding: '3px 12px',
                            borderRadius: '20px',
                            fontSize: '0.7rem',
                            fontWeight: '800',
                            letterSpacing: '0.5px',
                            backgroundColor: isDirect ? '#e6fffa' : '#fff3cd',
                            color: isDirect ? '#047481' : '#856404',
                            border: `1px solid ${isDirect ? '#b2f5ea' : '#ffeeba'}`,
                            textTransform: 'uppercase'
                        }}>
                            {isDirect ? providerName : `3RD PARTY (${providerName.toUpperCase()})`}
                        </span>
                    </div>

                    <div style={{ color: '#7f8c8d', fontSize: '0.85rem' }}>
                        📅 <strong>{t('reception.arrival')}:</strong> {checkInTimeString}
                    </div>
            
    
            
             
                                        <div style={{ display: "flex", gap: 10 }}>
                                            {!isFormOpen && (
                                                <button 
                                                    onClick={() => handleInitiateApproval(guest)}
                                                    className="btn-primary"
                                                    disabled={!!guestToApprove} 
                                                    style={{ padding: '8px 15px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                                                >
                                                    {t('reception.review_approve')}
                                                </button>
                                            )}
                                            {isFormOpen && (
                                                <button 
                                                    onClick={handleCloseForm}
                                                    className="btn-secondary"
                                                    style={{ padding: '8px 15px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                                                >
                                                    {t('reception.hide_form')}
                                                </button>
                                            )}

                                            <button 
                                                onClick={() => handleRejectGuest(guest.id, guest.fullName)}
                                                className="btn-delete"
                                                disabled={isFormOpen}
                                                style={{ padding: '8px 15px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                                            >
                                                {t('reception.reject')}
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* INLINE FORM RENDERING */}
                                    {isFormOpen && (
                                        <div style={{ padding: '20px', margin: '15px 0 0 0', borderTop: '1px solid #ccc' }}>
                                            <form onSubmit={handleFinalizeApproval} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                                
                                                {/* COLUMN 1: Guest Info & Room Assignment */}
                                                <div>
                                                    <h3 style={{ color: '#007bff', borderBottom: '1px solid #007bff', paddingBottom: '5px' }}>{t('reception.guest_info_title')}</h3>
                                                    <p><strong>{t('reception.full_name')}:</strong> {guest.fullName}</p>
                                                    <p><strong>{t('reception.email')}:</strong> {guest.email}</p>
                                                    <p><strong>{t('reception.phone')}:</strong> {guest.phone}</p>
                                                    <p><strong>{t('reception.arrival_departure')}:</strong> {safeToDate(guest.arrivalTime)?.toLocaleString() || 'N/A'} to {safeToDate(guest.departureTime)?.toLocaleString() || 'N/A'}</p>
                                                    <p><strong>{t('reception.guests')}:</strong> {guest.numGuests} | <strong>{t('reception.nationality')}:</strong> {guest.nationality || 'N/A'}</p>
                                                    <p style={{fontWeight: 'bold'}}>{t('reception.id_proof')}: <a href={guest.idProofUrl} target="_blank" rel="noopener noreferrer">{t('reception.view_doc')}</a></p>
                                                    
                                                    {/* >>> START GUEST PREFERENCES (FIXED) <<< */}
                                                    <div style={{ marginBottom: '20px', border: '1px dashed #ccc', padding: '10px', borderRadius: '5px', backgroundColor: '#f9f9f9', marginTop: '15px' }}>
                                                        <p style={{fontWeight: 'bold', margin: '0 0 5px 0', color: '#007bff'}}>{t('reception.preferences_title')}:</p>
                                                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9em' }}>
                                                            {/* FIX 1: Corrected field names for preferences */}
                                                            <li>**{t('reception.bed_type')}:** {guest.bedTypePreference || 'Not Specified'}</li>
                                                            <li>**{t('reception.floor_pref')}:** {guest.floorPreference || 'None'}</li>
                                                            <li>**{t('reception.quiet_high')}:** {guest.quietHighFloor === 'quiet' ? 'Quiet Room' : guest.quietHighFloor === 'high' ? 'High Floor' : 'No Preference'}</li>
                                                            <li>**{t('reception.early_checkin')}:** {guest.earlyCheckInRequest ? 'Requested' : 'No'}</li>
                                                            <li>**{t('reception.late_checkout')}:** {guest.lateCheckOutRequest ? 'Requested' : 'No'}</li>
                                                            <li>**{t('reception.accessibility')}:** {guest.accessibilityNeeds ? 'Yes' : 'No'}</li>
                                                            <li>**{t('reception.special_requests')}:** {guest.specialRequests || 'None'}</li>
                                                        </ul>
                                                    </div>
                                                    {/* >>> END GUEST PREFERENCES (FIXED) <<< */}
                                                    
                                                    {/* >>> START GUEST ADD-ONS (NEW FIX) <<< */}
                                                    <div style={{ marginBottom: '20px', border: '1px dashed #f0ad4e', padding: '10px', borderRadius: '5px', backgroundColor: '#fff8e1' }}>
                                                        <p style={{fontWeight: 'bold', margin: '0 0 5px 0', color: '#f0ad4e'}}>{t('reception.addons_requested')}:</p>
                                                        {requestedAddons.length > 0 ? (
                                                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9em' }}>
                                                                {requestedAddons.map(addon => (
                                                                    <li key={addon} style={{ textTransform: 'capitalize' }}>
                                                                        {formatAddonName(addon)}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <p style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9em', color: '#6c757d' }}>
                                                                {t('reception.no_addons')}
                                                            </p>
                                                        )}
                                                    </div>
                                                    {/* >>> END GUEST ADD-ONS (NEW FIX) <<< */}


                                                    {/* >>> START SIGNATURE FIX <<< */}
                                                    <div style={{ marginBottom: '20px', border: '1px dashed #ccc', padding: '10px', borderRadius: '5px', backgroundColor: '#f9f9f9' }}>
                                                        <p style={{fontWeight: 'bold', margin: '0 0 10px 0'}}>{t('reception.signature')}:</p>
                                                        {guest.signature ? (
                                                                <img 
                                                                    src={guest.signature} 
                                                                    alt="Guest Digital Signature"
                                                                    style={{ maxWidth: '100%', maxHeight: '150px', border: '1px solid #333', display: 'block' }}
                                                                />
                                                            ) : (
                                                                <p style={{ color: '#dc3545', margin: 0, fontSize: '0.9em' }}>
                                                                    {t('reception.no_signature')}
                                                                </p>
                                                            )}
                                                    </div>
                                                    {/* >>> END SIGNATURE FIX <<< */}

                                                    <h4 style={{ marginTop: '20px' }}>{t('reception.assign_room')}</h4>
                                                    <label>{t('reception.select_room_label')}:</label>
                                                    <select name="assignedRoomId" value={financialData.assignedRoomId} onChange={handleRoomAssignmentChange} required>
                                                        <option value="">{t('reception.select_room_placeholder')}</option>
                                                        {availableRooms.map(room => (
                                                            <option key={room.id} value={room.id}>
                                                                Room {room.roomNumber} - {room.type} (${parseFloat(room.price).toFixed(2)}/Night)
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <p style={{ fontSize: '0.9em', color: '#28a745' }}>{t('reception.assigned_room_status', { number: financialData.assignedRoomNumber || 'N/A', type: financialData.assignedRoomType })}</p>
                                                    
                                                </div>

                                                {/* COLUMN 2: Pricing, Add-ons & Access */}
                                                <div>
                                                    <h3 style={{ color: '#28a745', borderBottom: '1px solid #28a745', paddingBottom: '5px' }}>{t('reception.pricing_access_title')}</h3>

                                                    {/* Pricing Logic */}
                                                    <h4 style={{ color: '#333', marginTop: '10px' }}>{t('reception.billing_charges')}</h4>
                                                    
                                                    {/* >>> START PREPAID LOGIC <<< */}
                                                    <div style={{ marginBottom: '10px' }}>
                                                        <label style={{ display: 'block', fontWeight: 'bold' }}>{t('reception.acc_status')}:</label>
                                                        <label style={{ marginRight: '15px' }}>
                                                            <input 
                                                                type="radio" 
                                                                name="accommodationPrepaid" 
                                                                value={true} 
                                                                checked={financialData.accommodationPrepaid === true} 
                                                                onChange={() => handleFinancialChange({target: {name: 'accommodationPrepaid', type: 'checkbox', checked: true}})} 
                                                            /> {t('reception.prepaid')}
                                                        </label>
                                                        <label>
                                                            <input 
                                                                type="radio" 
                                                                name="accommodationPrepaid" 
                                                                value={false} 
                                                                checked={financialData.accommodationPrepaid === false} 
                                                                onChange={() => handleFinancialChange({target: {name: 'accommodationPrepaid', type: 'checkbox', checked: false}})} 
                                                            /> {t('reception.not_prepaid')}
                                                        </label>
                                                    </div>

                                                    {/* Input for Staff Accommodation Price (only shown if not prepaid) */}
                                                    {!financialData.accommodationPrepaid && (
                                                        <div style={{ marginBottom: '15px', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', backgroundColor: '#f0faff' }}>
                                                            <label style={{ display: 'block', fontWeight: 'bold' }}>{t('reception.staff_price_label')}:</label>
                                                            <input
                                                                type="text"
                                                                name="staffAccommodationPrice"
                                                                value={financialData.staffAccommodationPrice}
                                                                onChange={handleFinancialChange}
                                                                placeholder="0.00"
                                                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                                            />
                                                        </div>
                                                    )}
                                                    
                                                    {/* Add-ons Price (Guest Addons + Staff Addons total) */}
                                                    <div style={{ marginBottom: '15px' }}>
                                                        <label style={{ display: 'block', fontWeight: 'bold' }}>{t('reception.total_addons_label')}:</label>
                                                        <input
                                                            type="text"
                                                            name="addOnsPrice"
                                                            value={financialData.addOnsPrice}
                                                            onChange={handleFinancialChange}
                                                            placeholder="0.00"
                                                            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#f4f4f4' }}
                                                        />
                                                    </div>
                                                    
                                                    <h5 style={{ marginTop: '15px', marginBottom: '10px' }}>{t('reception.staff_addons_title')}:</h5>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
                                                        {ADDONS_LIST.map(addon => (
                                                            <label key={addon} style={{ display: 'flex', alignItems: 'center', backgroundColor: '#e9ecef', padding: '5px 10px', borderRadius: '3px' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={financialData.staffAddons[addon] || false}
                                                                    onChange={() => handleStaffAddonToggle(addon)}
                                                                    style={{ marginRight: '5px' }}
                                                                />
                                                                {formatAddonName(addon)}
                                                            </label>
                                                        ))}
                                                    </div>
                                                    
                                                    {/* Taxes and Deposits */}
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                                                        <div>
                                                            <label style={{ display: 'block', fontWeight: 'bold' }}>{t('reception.tax_rate')}:</label>
                                                            <input type="text" name="taxRate" value={(parseFloat(financialData.taxRate) * 100).toFixed(2)} onChange={(e) => handleFinancialChange({target: {name: 'taxRate', value: (parseFloat(e.target.value) / 100).toString(), type: 'text'}})} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                                                        </div>
                                                        <div>
                                                            <label style={{ display: 'block', fontWeight: 'bold' }}>{t('reception.tourist_tax')}:</label>
                                                            <input type="text" name="touristTax" value={financialData.touristTax} onChange={handleFinancialChange} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                                                        </div>
                                                        <div>
                                                            <label style={{ display: 'block', fontWeight: 'bold' }}>{t('reception.deposit')}:</label>
                                                            <input type="text" name="depositRequired" value={financialData.depositRequired} onChange={handleFinancialChange} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                                                        </div>
                                                    </div>


                                                    {/* Access and Final Payment */}
                                                    <h4 style={{ color: '#333', marginTop: '10px' }}>{t('reception.access_payment_title')}</h4>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                                        <div>
                                                            <label style={{ display: 'block', fontWeight: 'bold' }}>{t('reception.access_type')}:</label>
                                                            <select name="roomAccessType" value={financialData.roomAccessType} onChange={handleFinancialChange} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}>
                                                                <option>Digital Door Code</option>
                                                                <option>Keycard</option>
                                                            </select>
                                                        </div>
                                                        {financialData.roomAccessType === 'Digital Door Code' && (
                                                            <div>
                                                                <label style={{ display: 'block', fontWeight: 'bold' }}>{t('reception.door_code')}:</label>
                                                                <input type="text" name="doorCode" value={financialData.doorCode} onChange={handleFinancialChange} maxLength={6} placeholder="Leave blank to auto-generate" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    <div style={{ marginTop: '15px' }}>
                                                        <label style={{ display: 'block', fontWeight: 'bold' }}>{t('reception.payment_status')}:</label>
                                                        <select name="paymentStatus" value={financialData.paymentStatus} onChange={handleFinancialChange} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}>
                                                            <option>AWAITING_PAYMENT</option>
                                                            <option>PAID_IN_FULL</option>
                                                            <option>DEPOSIT_PAID</option>
                                                        </select>
                                                    </div>

                                                    <div style={{ marginTop: '15px' }}>
                                                        <label style={{ display: 'block', fontWeight: 'bold' }}>{t('reception.billing_notes')}:</label>
                                                        <textarea name="billingNotes" value={financialData.billingNotes} onChange={handleFinancialChange} rows="3" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                                                    </div>


                                                    {/* FINAL PRICE DISPLAY */}
                                                    <div style={{ backgroundColor: '#28a745', color: 'white', padding: '15px', borderRadius: '5px', textAlign: 'center', marginTop: '25px' }}>
                                                        <h3 style={{ margin: 0 }}>{t('reception.final_price_title')}</h3>
                                                        <h1 style={{ margin: '5px 0 0 0', fontSize: '2.5em' }}>${financialData.adjustedFinalPrice.toFixed(2)}</h1>
                                                        <p style={{ margin: 0, fontSize: '0.8em', opacity: 0.9 }}>
                                                            {t('reception.price_breakdown', { count: Math.max(1, Math.ceil((safeToDate(guest.departureTime)?.getTime() - safeToDate(guest.arrivalTime)?.getTime()) / 86400000)) })}
                                                        </p>
                                                    </div>
                                                    
                                                    <button 
                                                        type="submit" 
                                                        disabled={loading || !financialData.assignedRoomId} 
                                                        style={{ 
                                                            width: '100%', 
                                                            padding: '15px', 
                                                            backgroundColor: loading ? '#0056b3' : '#007bff', 
                                                            color: 'white', 
                                                            border: 'none', 
                                                            borderRadius: '5px', 
                                                            cursor: 'pointer', 
                                                            marginTop: '20px',
                                                            fontSize: '1.2em'
                                                        }}
                                                    >
                                                        {loading ? t('reception.processing') : t('reception.btn_approve_checkin')}
                                                    </button>

                                                </div>
                                            </form>
                                        </div>
                                    )}
                                </li>
                            </React.Fragment>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}